// Validation utility for VAT id numbers from countries
// where Reverb Payments is supported, based on
// https://en.wikipedia.org/wiki/VAT_identification_number#VAT_numbers_by_countries

interface Validation {
  (vatId: string): boolean;
}

// 'FR'+ 2 digits and/or letters + 9 digits - e.g. FRXX999999999
const isValidFR: Validation = vatId => /^FR[A-Z\d]{2}\d{9}$/.test(vatId);

// 9 digits, e.g. DE999999999
const isValidDE: Validation = vatId => /^DE\d{9}$/.test(vatId);

// 11 digits (the first 7 digits is a progressive number, the following 3
// means the province of residence, the last digit is a check number)
const isValidIT: Validation = vatId => /^IT\d{11}$/.test(vatId);

// 'NL'+9 digits+B+2-digit company index – e.g. NL999999999B99
const isValidNL: Validation = vatId => /^NL\d{9}B\d{2}$/.test(vatId);

// For companies it's either 'ES'+letter+8 digits or 'ES'+letter+7 digits+letter.
// Where the first letter defines the type of company and the following first 2 digits
// define the province where the company was registered.
// The last character is a control digit.
// For individual persons / freelancers, its either 'ES'+8 digits+letter (for Spaniards)
// or 'ES'+letter+7 digits+letter (for foreigners). e.g. ES99999999R
const isValidES: Validation = (vatId) => {
  const companyPattern = /^ES[A-Z]\d{7}[A-Z\d]$/;
  const individualPattern = /^ES(\d{8}[A-Z])|([A-Z]\d{7}[A-Z])$/;

  return companyPattern.test(vatId) || individualPattern.test(vatId);
};

// Country code GB followed by either:
// standard: 9 digits (block of 3, block of 4, block of 2 – e.g. GB999 9999 73)
// branch traders: 12 digits (same as for 9 digits, followed by a block of 3 digits)
// government departments: the letters GD then 3 digits from 000 to 499 (e.g. GBGD001)
// health authorities: the letters HA then 3 digits from 500 to 999 (e.g. GBHA599)
const isValidGB: Validation = (vatId) => {
  const standardPattern = /^GB\d{9}$/;
  const branchTraderPattern = /^GB\d{12}$/;
  const governmentPattern = /^GBGD[0-4]\d{2}$/;
  const healthAuthorityPattern = /^GBHA[5-9]\d{2}$/;

  return standardPattern.test(vatId) ||
    branchTraderPattern.test(vatId) ||
    governmentPattern.test(vatId) ||
    healthAuthorityPattern.test(vatId);
};

const ALL_WHITESPACE = /\s/g;

export const FRANCE = 'FR';
export const GERMANY = 'DE';
export const ITALY = 'IT';
export const NETHERLANDS = 'NL';
export const SPAIN = 'ES';
export const UNITED_KINGDOM = 'GB';

const isValidVatIdNumber: Validation = (vatId) => {
  const normalizedVatId = vatId.toUpperCase().replace(ALL_WHITESPACE, '');
  const countryCode = normalizedVatId.substr(0, 2);

  if (countryCode === FRANCE) {
    return isValidFR(normalizedVatId);
  }

  if (countryCode === GERMANY) {
    return isValidDE(normalizedVatId);
  }

  if (countryCode === ITALY) {
    return isValidIT(normalizedVatId);
  }

  if (countryCode === NETHERLANDS) {
    return isValidNL(normalizedVatId);
  }

  if (countryCode === SPAIN) {
    return isValidES(normalizedVatId);
  }

  if (countryCode === UNITED_KINGDOM) {
    return isValidGB(normalizedVatId);
  }

  return false;
};

export { isValidVatIdNumber };
